import { backofficeDialogOptions } from 'src/app/layout/shared/dialogs/backoffice-dialog.options';
import { Component, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SitesService } from 'src/app/core/services/api/sites.service';
import { CreateSiteRequest } from 'src/app/core/models/sites-management/create-site-request';
import { DynamicInputModalComponent } from '../dynamic-input-modal/dynamic-input-modal.component';
import { SiteBuildingStatusModalComponent } from '../site-building-status-modal/site-building-status-modal.component';
import { SidebarStateService } from 'src/app/core/services/template/sidebar-state.service';
import { getSiteRoutes } from '../../elements/side-bar/sidebar-menu-routes.config';

@Component({
  selector: 'app-no-sites',
  templateUrl: './no-sites.component.html',
  styleUrls: ['./no-sites.component.scss']
})
export class NoSitesComponent {

  @Output() createSite = new EventEmitter<void>();

  isCreating: boolean = false;
  private createdSite: any = null;

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private translate: TranslateService,
    private toastr: ToastrService,
    private sitesService: SitesService,
    private sidebarStateService: SidebarStateService
  ) {}

  onCreateSite() {
    // If a parent is listening (legacy usage), emit the event
    if (this.createSite.observers.length > 0) {
      this.createSite.emit();
      return;
    }

    // Otherwise, handle shop creation internally (routed page mode)
    this.openCreateShopModal();
  }

  private openCreateShopModal() {
    const modalRef = this.modalService.open(DynamicInputModalComponent, backofficeDialogOptions('shop-create', 'standard'));
    modalRef.componentInstance.modalTitle = this.translate.instant('sidebar.add_new_shop_modal_title');

    modalRef.result.then(
      (result) => {
        if (result) {
          this.isCreating = true;
          const newSite: CreateSiteRequest = {
            site_name: result,
            type: CreateSiteRequest.TypeEnum.STORE
          };

          // Ouvrir la modale de progression IMMÉDIATEMENT, en parallèle du POST,
          // pour qu'il n'y ait aucun temps mort après le clic sur "créer".
          const buildingModalRef = this.openBuildingStatusModal(result);

          this.sitesService.addNewSite(newSite).subscribe(
            (res: any) => {
              const createdSite = JSON.parse(JSON.stringify(res));
              this.isCreating = false;

              // Le site existe : la modale peut démarrer le polling de son statut
              this.createdSite = createdSite;
              buildingModalRef.componentInstance.beginTracking(
                createdSite.id,
                createdSite.site_name
              );
            },
            (error) => {
              this.isCreating = false;
              buildingModalRef.dismiss('create-failed');
              this.toastr.error(error.message, this.translate.instant('sidebar.shop_not_added'));
            }
          );
        }
      },
      () => {} // dismissed
    );
  }

  private openBuildingStatusModal(siteName: string): NgbModalRef {
    const buildingModalRef = this.modalService.open(SiteBuildingStatusModalComponent, { ...backofficeDialogOptions('site-build', 'medium'), keyboard: false });

    // Mode différé : le site n'existe pas encore, l'appelant fera beginTracking()
    buildingModalRef.componentInstance.deferStart = true;
    buildingModalRef.componentInstance.siteName = siteName;

    buildingModalRef.result.then(
      (buildingResult) => {
        const site = this.createdSite;
        if (buildingResult && buildingResult.success && site) {
          // Persist selection
          localStorage.setItem('selectedShopId', site.id);
          localStorage.setItem('selectedShopName', site.site_name);

          // Notify sidebar to refresh its list
          this.sidebarStateService.notifyListChanged();
          this.sidebarStateService.setSelectedShop(site);

          // Navigate to the new shop's dashboard
          this.router.navigate([
            getSiteRoutes(null).path + site.id + '/' + site.site_name
          ]);

          this.toastr.success(
            buildingResult.message || this.translate.instant('sidebar.site_created_success'),
            'Success'
          );
        }
      },
      () => {
        // Dismissed — refresh sidebar in case site was created
        this.sidebarStateService.notifyListChanged();
      }
    );

    return buildingModalRef;
  }

  contactSupport() {
    this.router.navigate(['/ecommerce/contact-us']);
  }
}
